// src/components/ui/ConfirmationModal.jsx
import React from "react";
import { X, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

export default function ConfirmationModal({
  open,
  onClose,
  title,
  message,
  type = "success", // success, error, info
  details,
  onConfirm,
  confirmLabel = "OK",
  showCancel = false,
  cancelLabel = "Cancel",
  loading = false,
}) {
  if (!open) return null;

  const iconMap = {
    success: <CheckCircle2 className="h-12 w-12 text-emerald-500" />,
    error: <AlertCircle className="h-12 w-12 text-red-500" />,
    info: <AlertCircle className="h-12 w-12 text-sky-500" />,
  };

  const bgColorMap = {
    success: "bg-emerald-50",
    error: "bg-red-50",
    info: "bg-sky-50",
  };

  const borderColorMap = {
    success: "border-emerald-200",
    error: "border-red-200",
    info: "border-sky-200",
  };

  return (
    <div className="fixed inset-0 z-[9999]">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700 transition-colors"
              disabled={loading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            <div className="flex flex-col items-center text-center">
              <div className={`rounded-full p-4 ${bgColorMap[type]} ${borderColorMap[type]} border-2 mb-4`}>
                {iconMap[type]}
              </div>
              <p className="text-base text-slate-700 font-medium mb-2">{message}</p>
              {details && (
                <div className="mt-4 w-full text-left">
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 max-h-64 overflow-y-auto">
                    {typeof details === "string" ? (
                      <p className="text-sm text-slate-600 whitespace-pre-wrap">{details}</p>
                    ) : (
                      <div className="space-y-2">
                        {details.map((detail, idx) => (
                          <p key={idx} className="text-sm text-slate-600">{detail}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-3">
            {showCancel && (
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
                disabled={loading}
              >
                {cancelLabel}
              </button>
            )}
            {onConfirm && (
              <button
                onClick={onConfirm}
                className={`px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2 ${
                  type === "success"
                    ? "bg-emerald-600 text-white hover:bg-emerald-700"
                    : type === "error"
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : "bg-sky-600 text-white hover:bg-sky-700"
                }`}
                disabled={loading}
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {confirmLabel}
              </button>
            )}
            {!onConfirm && (
              <button
                onClick={onClose}
                className={`px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 ${
                  type === "success"
                    ? "bg-emerald-600 text-white hover:bg-emerald-700"
                    : type === "error"
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : "bg-sky-600 text-white hover:bg-sky-700"
                }`}
                disabled={loading}
              >
                {confirmLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

